import React from "react";
import { CircularProgress, Paper, Stack, Typography } from "@mui/material";
import type { ParentChild } from "../../../types/parentFamily";
import NoticeboardButton from "../../shared/NoticeboardButton";
import { useChildCertificates } from "../../../hooks/useAwards";
import { formatSentDate } from "../../../domain/certificates/certificateDate";
import { awardeeFromFamilyRecord } from "../../../domain/certificates/awards";

interface ChildCertificatesSectionProps {
  child: ParentChild;
  /** The parent whose Family record this is; one of the child's linked parents. */
  parentUserId: string;
}

/**
 * A read-only list of the certificates sent for a child. They're found by
 * name and linked parent, so ones typed in before Family was on show up too.
 */
export const ChildCertificatesSection: React.FC<
  ChildCertificatesSectionProps
> = ({ child, parentUserId }) => {
  const {
    data: certificates = [],
    isLoading,
    isError,
    refetch,
  } = useChildCertificates(awardeeFromFamilyRecord(child, parentUserId));

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Certificates</Typography>
      {isLoading ? (
        <CircularProgress size={24} aria-label="Loading certificates" />
      ) : isError ? (
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography role="alert" variant="body2" color="error">
            Couldn't load this child's certificates.
          </Typography>
          <NoticeboardButton
            variant="tertiary"
            size="small"
            onClick={() => void refetch()}
          >
            Try again
          </NoticeboardButton>
        </Stack>
      ) : certificates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No certificates sent for this child yet.
        </Typography>
      ) : (
        certificates.map((certificate) => (
          <Paper
            key={certificate.certificateId}
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 2 }}
          >
            <Typography variant="body1">{certificate.awardTitle}</Typography>
            <Typography variant="body2" color="text.secondary">
              {[
                certificate.certificateTitle,
                certificate.certificateId,
                `Sent ${formatSentDate(certificate.sentAt)}`,
              ].join(" · ")}
            </Typography>
          </Paper>
        ))
      )}
    </Stack>
  );
};
